import { useEffect, useState, useCallback, useRef } from 'react';
import { convertSortFormat } from '../utils';
import { SortItem } from 'ali-react-table';

export interface IBackendPagingConfig {
  backendPaging?: boolean;
  extraParamsKey?: string;
}

interface IBackendPagingState {
  pageIndex: number;
  pageSize: number;
}

export function useBackendPaging(
  config: IBackendPagingConfig | undefined,
  changeData: ((key: string, value: Record<string, unknown>) => void) | undefined,
  extraParams: Record<string, unknown> | undefined,
  totalRecord: number,
) {
  const isEnabled = !!(config?.backendPaging && changeData);
  const extraParamsKey = config?.extraParamsKey || 'extraParams';

  const [pagingState, setPagingState] = useState<IBackendPagingState>({
    pageIndex: (extraParams?.page as number) || 1,
    pageSize: (extraParams?.page_size as number) || 10,
  });

  const isInitialMount = useRef(true);
  const latestExtraParams = useRef(extraParams);
  latestExtraParams.current = extraParams;

  const emitChangeData = useCallback(
    (page: number, pageSize: number, sorts?: SortItem[]) => {
      if (!changeData) return;
      const merged: Record<string, unknown> = {
        ...(latestExtraParams.current || {}),
        page,
        page_size: pageSize,
      };
      if (sorts) {
        merged.multi_sort = convertSortFormat(sorts, 'backend');
      }
      changeData(extraParamsKey, merged);
    },
    [changeData, extraParamsKey],
  );

  const handlePagingChange = useCallback(
    (e: { pageIndex?: number; pageSize?: number }) => {
      const newPageIndex = e.pageIndex ?? pagingState.pageIndex;
      const newPageSize = e.pageSize ?? pagingState.pageSize;
      const pageChanged = newPageIndex !== pagingState.pageIndex;
      const sizeChanged = newPageSize !== pagingState.pageSize;

      if (!pageChanged && !sizeChanged) return;

      const finalPageIndex = sizeChanged ? 1 : newPageIndex;
      setPagingState({ pageIndex: finalPageIndex, pageSize: newPageSize });
      emitChangeData(finalPageIndex, newPageSize);
    },
    [pagingState, emitChangeData],
  );

  const handleSortChange = useCallback(
    (sorts: SortItem[]) => {
      setPagingState(prev => ({ ...prev, pageIndex: 1 }));
      emitChangeData(1, pagingState.pageSize, sorts);
    },
    [pagingState.pageSize, emitChangeData],
  );

  useEffect(() => {
    if (!isEnabled) return;
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    if (totalRecord > 0 && pagingState.pageIndex > 1) {
      const maxPage = Math.ceil(totalRecord / pagingState.pageSize);
      if (pagingState.pageIndex > maxPage) {
        setPagingState(prev => ({ ...prev, pageIndex: 1 }));
        emitChangeData(1, pagingState.pageSize);
      }
    }
  }, [totalRecord]);

  if (!isEnabled) return null;

  return {
    pageIndex: pagingState.pageIndex,
    pageSize: pagingState.pageSize,
    onPageSizeChange: handlePagingChange,
    onSortChange: handleSortChange,
  };
}
